import { Injectable } from '@angular/core';
import { HttpClientService } from '@core/services/http-client/http-client.service';
import { API_URL_CONSTANT } from '@sharedModule/constants/api-url.constant';
import { DataGrid } from '@sharedModule/models/data-grid.model';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { removeEmptyStrings } from '@sharedModule/utils/form.utils';
import {
  BehaviorSubject,
  Observable,
  map,
  tap,
  shareReplay,
  catchError,
  throwError,
} from 'rxjs';
import { PartnerListInterface } from '../types/partner-list';
import {
  PartnerListFilter,
  defaultPartnerListFilter,
} from '../types/partner-list-filter';
import {
  AddPartnerInterface,
  UpdatePartnerInterface,
} from '../types/upsert-partner';

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private dataSubject = new BehaviorSubject<PartnerListInterface[]>([]);
  public sharedData$ = this.dataSubject.asObservable();
  private cachedData$: Observable<PartnerListInterface[]> | null = null;

  constructor(private httpClient: HttpClientService) {}

  getPartnerList(
    payload: PartnerListFilter = defaultPartnerListFilter
  ): Observable<ResponseIdentity<DataGrid<PartnerListInterface[]>>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partner.getPartnerList,
      removeEmptyStrings(payload)
    );
  }

  getPartnerListDropdown(
    forceRefresh = false
  ): Observable<PartnerListInterface[]> {
    if (forceRefresh) this.cachedData$ = null;
    if (this.cachedData$) return this.cachedData$;
    const request = this.httpClient.post(
      API_URL_CONSTANT.partner.getPartnerList,
      {}
    ) as Observable<{ response: PartnerListInterface[] }>;
    this.cachedData$ = request.pipe(
      map(data => data.response),
      tap(partners =>
        this.dataSubject.next(
          partners.filter(
            (partner: PartnerListInterface) => partner.is_active === true
          )
        )
      ),
      catchError(error => {
        this.cachedData$ = null;
        return throwError(() => error);
      }),
      shareReplay(1)
    );
    return this.cachedData$;
  }

  getPartnerDetails(payload: {
    partner_id: number;
  }): Observable<ResponseIdentity<PartnerListInterface>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partner.getPartnerDetails,
      payload
    );
  }

  addPartner(payload: AddPartnerInterface): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(API_URL_CONSTANT.partner.addPartner, payload);
  }

  updatePartner(
    payload: UpdatePartnerInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partner.updatePartner,
      payload
    );
  }

  updatePartnerStatus(payload: {
    partner_id: number;
    is_active: boolean;
  }): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partner.updatePartnerStatus,
      payload
    );
  }

  deletePartner(payload: {
    partner_id: number;
  }): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partner.deletePartner,
      payload
    );
  }
}
